import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { AdaptoActions } from '@frontend/adapto-actions';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppFlagStore } from '@frontend/shared';
import { ActionButton, useTooltip } from '@frontend/design-system';
import { useReviewAction } from '../hooks';
import { ReviewActionFlowProps } from '../types';
import { ReviewActionModal } from './review-action-modal';

type ReviewActionProps = ReviewActionFlowProps & {
  trackingIdSuffix?: string;
};
export const ReviewActionButton = ({ groupId, personId, appointmentId, trackingIdSuffix }: ReviewActionProps) => {
  const { t } = useTranslation('contact-actions-review-invitation');
  const reviewInvitationProps = useReviewAction({
    groupId,
    personId,
    appointmentId,
  });

  const { featureFlags } = useAppFlagStore();
  const canSendMessages = featureFlags[groupId]?.get('nwx:messaging-integrated-messaging-fe-acl-enforcement')?.value
    ? hasSchemaACL(groupId, Permission.SMS_INBOX_MANAGE)
    : true;

  const { Tooltip, tooltipProps, triggerProps } = useTooltip();

  if (reviewInvitationProps === undefined) return null;

  const isDisabled = !canSendMessages || reviewInvitationProps.isValidatingPhone;

  return (
    <>
      {!canSendMessages && <Tooltip {...tooltipProps}>{t('You do not have permission to send text messages')}</Tooltip>}
      <ActionButton
        {...reviewInvitationProps.triggerProps}
        trackingId={`${reviewInvitationProps.triggerProps.trackingId}${trackingIdSuffix ?? ''}`}
        disabled={isDisabled}
        {...(!canSendMessages ? triggerProps : {})}
      >
        <Icon name='star-small' />
      </ActionButton>
      <ReviewActionModal {...reviewInvitationProps.modalProps} />
    </>
  );
};

export interface ReviewAdaptoActionProps extends ReviewActionProps {
  label: string;
  disabled?: boolean;
  hoverLabel?: string;
}

export const ReviewAction = ({ groupId, personId, trackingIdSuffix, ...rest }: ReviewAdaptoActionProps) => {
  const { t } = useTranslation('contact-actions-review-invitation');
  const reviewInvitationProps = useReviewAction({
    groupId,
    personId,
  });
  const { featureFlags } = useAppFlagStore();
  const canSendMessages = featureFlags[groupId]?.get('nwx:messaging-integrated-messaging-fe-acl-enforcement')?.value
    ? hasSchemaACL(groupId, Permission.SMS_INBOX_MANAGE)
    : true;

  if (reviewInvitationProps === undefined) return null;

  const isDisabled = !canSendMessages || reviewInvitationProps.isValidatingPhone;

  return (
    <>
      <AdaptoActions.Action
        {...rest}
        {...reviewInvitationProps.triggerProps}
        trackingId={`${reviewInvitationProps.triggerProps.trackingId}${trackingIdSuffix ?? ''}`}
        icon='star'
        disabled={isDisabled || rest.disabled}
        hoverLabel={
          rest.disabled
            ? rest.hoverLabel
            : canSendMessages
            ? undefined
            : t('You do not have permission to send text messages')
        }
        showLabelWhenDisabled
      />
      <ReviewActionModal {...reviewInvitationProps.modalProps} />
    </>
  );
};
